import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search, X } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../ui';
import { Applicant } from '../../../../../utils/types/JobOpenings';

export interface ISearchResultsProps {
  setShowSearchPanel: (show: boolean) => void;
  filteredCandidates: Applicant[];
  getApplicantInitials: (candidate: Applicant) => string;
  handleViewApplicantProfile: (candidate: Applicant) => void;
}

export const SearchResults = ({
  setShowSearchPanel,
  filteredCandidates,
  handleViewApplicantProfile,
}: ISearchResultsProps) => {
  return (
    <Paper
      elevation={2}
      sx={{
        width: 320,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
        height: 'fit-content',
        maxHeight: 'calc(100vh - 240px)',
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%)',
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#059669',
              }}
            >
              <Search size={16} />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary' }}
            >
              Search Results
            </Typography>
          </Stack>
          <Tooltip title="Close Search Panel">
            <IconButton
              size="small"
              onClick={() => setShowSearchPanel(false)}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'text.primary' },
              }}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.75,
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '12px',
          }}
        >
          {filteredCandidates.length} candidate
          {filteredCandidates.length !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {/* Scrollable Mid-Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          maxHeight: 380,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(0, 0, 0, 0.15)',
            borderRadius: '3px',
          },
        }}
      >
        {filteredCandidates.map((candidate) => {
          const fullName =
            candidate.name || `${candidate.firstName} ${candidate.lastName}`;

          return (
            <Box
              key={candidate.userId}
              onClick={() => handleViewApplicantProfile(candidate)}
              sx={{
                p: 1.5,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(37, 99, 235, 0.04)',
                },
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <NameEmailAvatar
                applicantStatus={candidate.applicantStatus}
                avatar={candidate.lastName || undefined}
                name={fullName}
                email={candidate.email}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
